import { useBillingModel } from '@/modules/billing/hooks/useBillingModel';
import { contractInitialState } from '@/modules/contract/entities/contants/contract';
import { Contract } from '@/modules/contract/entities/interfaces/contract';
import Infos, { InfoKey } from '@/modules/contract/views/infos/infos';
import Plans from '@/modules/contract/views/plans/plans';
import Rules from '@/modules/contract/views/rules/rules';
import { Negotiation } from '@/modules/negotiation/entities/interfaces/negotiation';
import { PlanWithNegotiation } from '@/modules/plan/entities/intefaces/plan';
import { useHistoryNavigator, useHistoryParams } from '@/navigation';
import { contractsService, negotiationsService } from '@/services/negotiations';
import useCompany from '@/shared/hooks/useCompany/useCompany';
import { tryOrCatchMessageError } from '@/utils/tryOrCatchMessageError';
import { get } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

const Details: React.FC = () => {
  const navigate = useHistoryNavigator();
  const [ loading, setLoading ] = useState(false);
  const [ contract, setContract ] = useState<Contract>(contractInitialState);
  const [ negotiations, setNegotiations ] = useState<Negotiation[]>([]);
  const { contractId } = useHistoryParams();
  const { company } = useCompany();
  const [ currentCompanyId ] = useState(company.id);
  const { isCompanyFeeForService } = useBillingModel();

  const ruleTypesByBenefit = useMemo(
    () => JSON.parse(process.env.REACT_APP_RULE_TYPES || '{}'),
    [],
  );

  const baseId = useMemo(() => {
    const negotiation = negotiations.find((item) => {
      return Object.keys(ruleTypesByBenefit).some(
        (key) => String(key) === String(get(item, 'product.benefit.baseId', '')),
      );
    });
    return get(negotiation, 'product.benefit.baseId', '');
  }, [ negotiations, ruleTypesByBenefit ]);

  const plans = useMemo(() => {
    return negotiations.map((negotiation) => ({
      ...negotiation.product,
      negotiation,
    }));
  }, [ negotiations ]);

  const infoItems = isCompanyFeeForService
    ? [
      { key: InfoKey.PROVIDER, size: 75 },
      { key: InfoKey.STATUS, size: 25 },
      { key: InfoKey.CODE, size: 25 },
      { key: InfoKey.CONTRIBUTION, size: 25 },
      { key: InfoKey.COPAY, size: 25 },
      { key: InfoKey.ENROLLMENT_CODE, size: 25 },
      { key: InfoKey.VALID_FROM, size: 25 },
      { key: InfoKey.VALID_UNTIL, size: 25 },
      { key: InfoKey.MODAL_NAME, size: 25 },
      { key: InfoKey.REIMBURSEMENT_COEFFICIENT, size: 25 },
    ]
    : [
      { key: InfoKey.PROVIDER, size: 75 },
      { key: InfoKey.STATUS, size: 25 },
    ];

  const loadNegotiation = useCallback(async () => {
    return tryOrCatchMessageError(
      async () => {
        if (!company.id) {
          return;
        }

        const negotiationsFound =
          (await negotiationsService(company.id).list({ contractId })) || [];

        setNegotiations(negotiationsFound);
      },
      {
        messageErrorDefault: 'Erro ao carregar negociação.',
        tryBefore: () => setLoading(true),
        tryAfter: () => setLoading(false),
      },
    );
  }, [ company.id, contractId ]);

  const loadContract = useCallback(async () => {
    return tryOrCatchMessageError(
      async () => {
        if (!company.id) {
          return;
        }

        const contractFound = (await contractsService(company.id).find(contractId)) || [];

        setContract(contractFound);
      },
      {
        messageErrorDefault: 'Erro ao carregar contrato.',
        tryBefore: () => setLoading(true),
        tryAfter: () => setLoading(false),
      },
    );
  }, [ company.id, contractId ]);

  useEffect(() => {
    loadContract();
    loadNegotiation();
  }, [ loadContract, loadNegotiation ]);

  useEffect(() => {
    if (company.id && currentCompanyId && company.id !== currentCompanyId) {
      navigate.push(`/companies/${ company.id }/contracts`);
    }
  }, [ company.id, currentCompanyId, navigate ]);

  return (
    <>
      <Infos
        loading={ loading }
        contract={ contract }
        items={ infoItems }
      />

      { ruleTypesByBenefit[baseId] && isCompanyFeeForService ? (
        <Rules
          contractId={ contractId }
          items={ contract.rules || [] }
          ruleTypes={ ruleTypesByBenefit[baseId] || [] }
          baseId={ String(baseId) }
        />
      ) : null }

      <Plans data={ plans as PlanWithNegotiation[] } />
    </>
  );
};

export default Details;
